import { useEffect, useState, useRef } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { Download, Upload, Loader2, Clock, Users, Package } from "lucide-react";

function BackupCard({ kind, icon: Icon, title, desc, count, onChanged }) {
  const [busy, setBusy] = useState(null);           // "download" | "restore"
  const [mode, setMode] = useState("merge");        // "merge" | "replace"
  const [confirm, setConfirm] = useState(null);     // parsed payload awaiting replace confirmation
  const fileRef = useRef(null);
  const label = kind === "users" ? "users" : "ads";

  const download = async () => {
    setBusy("download");
    try {
      const res = await api.get(`/admin/backup/${kind}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : `critterposts-${kind}.json`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${title} backup downloaded`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(null);
    }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    let payload;
    try {
      payload = JSON.parse(await file.text());
    } catch {
      return toast.error("That file isn't valid JSON");
    }
    const records = Array.isArray(payload) ? payload : payload?.records;
    if (!Array.isArray(records)) return toast.error("Backup file has no 'records' array");
    if (payload?.type && payload.type !== kind) {
      return toast.error(`This looks like a '${payload.type}' backup, not ${kind}`);
    }
    if (mode === "replace") {
      // Preview what a wipe & replace would change before asking to confirm.
      setBusy("restore");
      try {
        const { data } = await api.post(`/admin/restore/${kind}/preview`, { payload });
        setConfirm({ payload, preview: data });
      } catch (err) {
        toast.error(errText(err));
      } finally {
        setBusy(null);
      }
      return;
    }
    doRestore(payload);
  };

  const doRestore = async (payload) => {
    setConfirm(null);
    setBusy("restore");
    try {
      const { data } = await api.post(`/admin/restore/${kind}`, { mode, payload });
      const skip = data.skipped ? ` (${data.skipped} skipped)` : "";
      toast.success(`Restored ${data.restored} ${label} record(s) (${mode})${skip}. Total now: ${data.total_now}.`);
      onChanged?.();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col" data-testid={`backup-card-${kind}`}>
      <div className="flex items-center gap-3">
        <span className="h-11 w-11 rounded-xl bg-[#FFF4F0] flex items-center justify-center text-[#F5551E]"><Icon size={22} /></span>
        <div>
          <p className="font-display font-bold text-[#12234A]">{title}</p>
          <p className="text-xs text-slate-400" data-testid={`backup-count-${kind}`}>{count == null ? "…" : `${count} record${count === 1 ? "" : "s"}`}</p>
        </div>
      </div>
      <p className="text-sm text-slate-500 mt-3 flex-1">{desc}</p>

      <Button onClick={download} disabled={busy === "download"} data-testid={`backup-download-${kind}`}
        className="mt-4 rounded-xl bg-[#12234A] hover:bg-[#0d1a38] text-white gap-2">
        {busy === "download" ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
        {busy === "download" ? "Preparing…" : `Download ${label} backup`}
      </Button>

      <div className="mt-4 pt-4 border-t border-slate-100">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Restore from a backup file</p>
        <div className="flex rounded-full bg-slate-100 p-0.5 mb-3 w-full" data-testid={`restore-mode-${kind}`}>
          {[["merge", "Merge (safe)"], ["replace", "Wipe & replace"]].map(([val, lbl]) => (
            <button key={val} onClick={() => setMode(val)} data-testid={`restore-mode-${kind}-${val}`}
              className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-full transition-colors ${mode === val ? (val === "replace" ? "bg-red-600 text-white" : "bg-[#F5551E] text-white") : "text-slate-500 hover:text-[#12234A]"}`}>
              {lbl}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400 mb-3">
          {mode === "merge"
            ? "Re-adds missing records and updates existing ones. Nothing is deleted."
            : "Empties the dataset first, then loads only what's in the file. Destructive."}
        </p>
        <input ref={fileRef} type="file" accept="application/json,.json" hidden onChange={onPick} data-testid={`restore-file-${kind}`} />
        <Button onClick={() => fileRef.current?.click()} disabled={busy === "restore"} variant="outline" data-testid={`restore-btn-${kind}`}
          className={`w-full rounded-xl gap-2 ${mode === "replace" ? "border-red-200 text-red-600 hover:bg-red-50" : ""}`}>
          {busy === "restore" ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {busy === "restore" ? "Restoring…" : `Restore ${label}…`}
        </Button>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Wipe &amp; replace all {label}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>This replaces every current {label} record with the contents of this file. This cannot be undone.</p>
                {confirm?.preview && (
                  <div className="grid grid-cols-3 gap-2 text-center" data-testid={`restore-preview-${kind}`}>
                    <div className="rounded-lg bg-emerald-50 py-2"><p className="text-lg font-bold text-emerald-600" data-testid={`preview-add-${kind}`}>{confirm.preview.add}</p><p className="text-[11px] text-slate-500">Added</p></div>
                    <div className="rounded-lg bg-blue-50 py-2"><p className="text-lg font-bold text-blue-600" data-testid={`preview-update-${kind}`}>{confirm.preview.update}</p><p className="text-[11px] text-slate-500">Updated</p></div>
                    <div className="rounded-lg bg-red-50 py-2"><p className="text-lg font-bold text-red-600" data-testid={`preview-remove-${kind}`}>{confirm.preview.remove}</p><p className="text-[11px] text-slate-500">Removed</p></div>
                  </div>
                )}
                {confirm?.preview && (
                  <p className="text-xs text-slate-400">File has {confirm.preview.file_count} record(s); you currently have {confirm.preview.current_count}.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => doRestore(confirm.payload)} data-testid={`restore-confirm-${kind}`} className="rounded-xl bg-red-600 hover:bg-red-700">Wipe &amp; replace</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BackupHistory({ refreshKey }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/backup/history").then((r) => setItems(r.data)).catch(() => setItems([])); }, [refreshKey]);

  const download = async (id) => {
    try {
      const res = await api.get(`/admin/backup/history/${id}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : `critterposts-backup-${id}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errText(e)); }
  };

  if (!items) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="backup-history">
      <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2"><Clock size={16} className="text-[#F5551E]" /> Backup history</h3>
      <p className="text-xs text-slate-400 mt-1">Automatic nightly snapshots and manual saves (last 14 kept).</p>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400 mt-3" data-testid="backup-history-empty">No stored backups yet. A snapshot is saved automatically every night.</p>
      ) : (
        <div className="mt-3 divide-y divide-slate-100">
          {items.map((b) => (
            <div key={b.id} className="flex items-center justify-between py-2.5" data-testid={`history-row-${b.id}`}>
              <div>
                <p className="text-sm font-semibold text-[#12234A]">{new Date(b.created_at).toLocaleString()}</p>
                <p className="text-xs text-slate-400">
                  <span className={`inline-block px-1.5 py-0.5 rounded mr-1.5 ${b.source === "nightly" ? "bg-slate-100 text-slate-500" : "bg-[#FFF4F0] text-[#F5551E]"}`}>{b.source}</span>
                  {Object.entries(b.counts || {}).map(([k, v]) => `${v} ${k}`).join(" · ")}
                </p>
              </div>
              <Button onClick={() => download(b.id)} variant="outline" size="sm" data-testid={`history-download-${b.id}`} className="rounded-xl gap-1.5"><Download size={14} /> ZIP</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function BackupTab() {
  const [counts, setCounts] = useState(null);
  const [busySnap, setBusySnap] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const load = () => api.get("/admin/backup/summary").then((r) => setCounts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const downloadFull = async () => {
    setBusySnap("download");
    try {
      const res = await api.get("/admin/backup/snapshot", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : "critterposts-full-backup.zip";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Full backup downloaded");
    } catch (e) { toast.error(errText(e)); }
    finally { setBusySnap(null); }
  };

  const saveSnapshot = async () => {
    setBusySnap("save");
    try {
      await api.post("/admin/backup/snapshot-now");
      toast.success("Snapshot saved to history");
      setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusySnap(null); }
  };

  return (
    <div className="space-y-4" data-testid="backup-tab">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="font-display font-bold text-lg text-[#12234A] flex items-center gap-2"><Download size={18} className="text-[#F5551E]" /> Data backup &amp; restore</h2>
        <p className="text-sm text-slate-500 mt-1">Download complete, timestamped snapshots of your data — keep them safe. A full backup is saved automatically every night and emailed to admins. If the site ever goes down, relaunch it and restore the files here.</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button onClick={downloadFull} disabled={busySnap === "download"} data-testid="backup-download-full" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-2">
            {busySnap === "download" ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Download everything (ZIP)
          </Button>
          <Button onClick={saveSnapshot} disabled={busySnap === "save"} variant="outline" data-testid="backup-snapshot-now" className="rounded-xl gap-2">
            {busySnap === "save" ? <Loader2 size={16} className="animate-spin" /> : <Clock size={16} />} Save snapshot to history
          </Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <BackupCard kind="users" icon={Users} title="Users & Profiles" desc="Every account, role, login credential and profile field — restorable so members can sign in again." count={counts?.users} onChanged={load} />
        <BackupCard kind="listings" icon={Package} title="Ads / Listings" desc="Every listing with its details, photos and status." count={counts?.listings} onChanged={load} />
      </div>
      <BackupHistory refreshKey={refreshKey} />
    </div>
  );
}
